import React, { useEffect, useRef } from 'react'

import * as S from './styles'
import { DefaultImage } from '@/service/types'

type ImageMatrix = {
  images: DefaultImage[]
}

export type ImageSlideContainerProps = {
  /**
   * Contains the images
   * @example
   * ```tsx
   * <ImageSlideContainer content={content} />
   * ```
   * */
  content: ImageMatrix[]

  /**
   * Selected matrix index
   * @example
   * ```tsx
   * <ImageSlideContainer currentIndex={0} />
   * ```
   * */
  currentIndex: number
}

const fadeInClass = 'fade-in'
const fadeOutClass = 'fade-out'

const fadeInAndOutDelay = 1000

export const ImageSlideContainer: React.FC<ImageSlideContainerProps> = ({
  content,
  currentIndex,
}) => {
  const firstRun = useRef(true)
  const indexRef = useRef<number>(0)

  const stateRef = useRef({
    cases: content.map((el) =>
      new Array(el.images.length).fill(0).map(() => ({} as HTMLImageElement)),
    ),
  })

  const timeout = async (ms: number) => {
    await new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve()
      }, ms)
    })
  }

  useEffect(() => {
    if (!content.length) return
    const clear = () => {
      const { cases } = stateRef.current

      cases.forEach((page, i) => {
        if (i !== currentIndex) {
          page.forEach((image) => {
            if (!image.hidden) {
              image.hidden = true
              image.classList.remove(fadeOutClass, fadeInClass)
            }
          })
        }
      })
    }
    const fadeIn = async () => {
      // first run should run immediately
      if (!firstRun.current) {
        await timeout(fadeInAndOutDelay)
      }

      clear() // interrupt fadeOut if needed

      const images = stateRef.current.cases[currentIndex]

      images.forEach((image) => {
        image.hidden = false
        image.classList.remove(fadeOutClass)
      })

      images.map(async (image, i) => {
        if (image) {
          await timeout(150 * i)
          image.classList.add(fadeInClass)
        }
      })

      firstRun.current = false
    }

    const fadeOut = async () => {
      const images = stateRef.current.cases[indexRef.current]

      await Promise.all(
        images.map(async (image, i) => {
          if (image) {
            await timeout(150 * i)
            image.classList.add(fadeOutClass)
            image.classList.remove(fadeInClass)
          }
        }),
      )
      await timeout(200)
      images.forEach((image) => {
        if (image) {
          image.hidden = true
        }
      })
    }

    if (!firstRun.current) {
      fadeOut()
    }
    fadeIn()
    indexRef.current = currentIndex
  }, [currentIndex, content.length])

  return (
    <S.FadeInContainer>
      {content &&
        content.map((el, caseIndex) =>
          el.images.map((image, imageIndex) => (
            <S.SlideImage
              src={image.url || ''}
              alt={image.alternativeText || image.name}
              key={image.url}
              height={image.height}
              width={image.width}
              priority
              ref={(element) => {
                if (element) {
                  if (stateRef.current.cases[caseIndex]) {
                    stateRef.current.cases[caseIndex][imageIndex] = element
                  } else {
                    stateRef.current.cases[caseIndex] = []
                  }
                }
              }}
              unoptimized={process.env.VERCEL_ENV !== 'production'}
              hidden
            />
          )),
        )}
    </S.FadeInContainer>
  )
}
